import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Button, Stack, Tabs, Tab } from 'react-bootstrap';
import { isUndefined, isNull } from 'lodash';
import { useTranslation } from 'react-i18next';
import pluralize from 'pluralize';

import { selectIsFrontline, selectIsInsurer } from '@components/Auth';
import { BackButton, ClaimBadge, DownloadLinkButton, Loading, LoadingError, NoData } from '@components/Base';
import { ClaimableTypes } from '@models/InsuranceClaim';
import { amountToUsd } from '@utils/currency';
import { formatDateTime, formatDate } from '@utils/datetime';
import { ActionRequestTabContent } from '@components/Feature/ActionRequest';
import { ActionRequestSubjectTypes } from '@models/ActionRequest';

import { useEditable } from '../hook';
import { InsuranceClaimEditModal } from '../../InsuranceClaimEditModal';
import { selectHealthClaim } from './selector';
import { healthClaimFetch } from './action';
import { DocumentsTabContent } from './documents-tab-content';
import CostEstimatesTabContent from './CostEstimatesTabContent';
import { SettlementApprovalTabContent } from './settlement-approval-tab-content';
import { HealthClaimDetailsEditModal } from './health-claim-details-edit-modal';
import { RowItem } from './row-item';
import { ClaimDetailsTabContent } from './claim-details-tab-content';

const HealthClaimDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { claimableId } = useParams();
  const isFrontline = useSelector(selectIsFrontline);
  const isInsurer = useSelector(selectIsInsurer);
  const { healthClaim } = useSelector(selectHealthClaim);
  const isLoaded = !isUndefined(healthClaim);
  const isError = isNull(healthClaim);
  const hasHealthClaim = isLoaded && !isNull(healthClaim);
  const shouldEdit = useEditable(healthClaim);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsEditModalOpen, setIsDetailsEditModalOpen] = useState(false);

  const handleReload = () => {
    if (claimableId) {
      dispatch(healthClaimFetch({ id: Number(claimableId) }));
    }
  };

  useEffect(() => {
    if (claimableId) {
      dispatch(healthClaimFetch({ id: Number(claimableId) }));
    }
  }, [claimableId]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isLoaded && <Loading />}
      {isError && <LoadingError />}
      {isLoaded && !hasHealthClaim && <NoData />}

      {healthClaim && (
        <Card>
          <Card.Header>
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <h5 className="mb-0 me-auto">{t('common:details')}</h5>
              <DownloadLinkButton href={healthClaim.claimSubmission} size="sm">
                {t('common:downloadClaimSubmission')}
              </DownloadLinkButton>
              {shouldEdit && (
                <Button size="sm" onClick={() => setIsDetailsEditModalOpen(true)}>
                  {`${t('common:edit')} ${t('common:details')}`}
                </Button>
              )}
            </Stack>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6} className="mb-3">
                <RowItem label={t('insuranceClaimDetails.billingType')} value={healthClaim.billingTypeCode} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem
                  label={t('insuranceClaimDetails.status')}
                  value={
                    <Stack direction="horizontal" gap={2} className="align-items-center">
                      <ClaimBadge status={healthClaim.status} />
                      {shouldEdit && (
                        <Button size="sm" onClick={() => setIsModalOpen(true)}>
                          {t('common:edit')}
                        </Button>
                      )}
                    </Stack>
                  }
                />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('insuranceClaimDetails.claimNumber')} value={healthClaim.claimNumber} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem
                  label={t('insuranceClaimDetails.registerDate')}
                  value={formatDateTime(healthClaim.createdAt)}
                />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem
                  label={t('insuranceClaimDetails.totalClaimedAmount')}
                  value={amountToUsd(healthClaim.totalClaimedAmount)}
                />
              </Col>
              {Number(healthClaim.approvedAmount) > 0 && (
                <Col md={6} className="mb-3">
                  <RowItem
                    label={t('claimForm.approvedAmount.label')}
                    value={amountToUsd(healthClaim.approvedAmount)}
                  />
                </Col>
              )}
              <Col md={6} className="mb-3">
                <RowItem label={t('common:policyNumber')} value={healthClaim.policyNumber} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('healthClaimDetails.companyName')} value={healthClaim.companyName} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('common:planType')} value={healthClaim.benefitTemplateName} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('healthClaimDetails.memberName')} value={healthClaim.insurancePolicyUserName} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem
                  label={t('common:periodOfInsurance')}
                  value={`${formatDate(healthClaim.insurancePolicyUserEffectiveDate)} - ${formatDate(
                    healthClaim.insurancePolicyUserExpirationDate,
                  )}`}
                />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('common:creator')} value={healthClaim.creator?.name} />
              </Col>
              {healthClaim.note && (
                <Col md={6} className="mb-3">
                  <RowItem label={t('common:note')} value={healthClaim.note} />
                </Col>
              )}
            </Row>
          </Card.Body>

          <Card.Body>
            <Tabs defaultActiveKey="document" className="mb-3">
              <Tab eventKey="document" title={pluralize(t('insuranceClaimDetails.document.title'))}>
                <DocumentsTabContent />
              </Tab>
              <Tab eventKey="actionRequest" title="Requests">
                <ActionRequestTabContent
                  subjectType={ActionRequestSubjectTypes.InsuranceClaim}
                  subjectId={healthClaim.insuranceClaimId}
                  canCreate={isFrontline || isInsurer}
                />
              </Tab>
              <Tab eventKey="claim-details" title={t('common:claimDetails')}>
                <ClaimDetailsTabContent healthClaim={healthClaim} />
              </Tab>
              {isFrontline && (
                <Tab eventKey="cost-estimates" title="Cost estimates">
                  <CostEstimatesTabContent />
                </Tab>
              )}
              {isFrontline && (
                <Tab eventKey="settlement-approval" title="Settlement & Approval">
                  <SettlementApprovalTabContent />
                </Tab>
              )}
            </Tabs>
          </Card.Body>
        </Card>
      )}

      {healthClaim && (
        <>
          <InsuranceClaimEditModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            claimNumber={healthClaim.claimNumber}
            claimableType={ClaimableTypes.Health}
            claimableId={healthClaim.id}
            currentStatus={healthClaim.status}
            currentTotalClaimedAmount={healthClaim.totalClaimedAmount}
            currentNote={healthClaim.note}
            currentApprovedAmount={healthClaim.approvedAmount}
            onSuccess={handleReload}
          />
          <HealthClaimDetailsEditModal
            isOpen={isDetailsEditModalOpen}
            healthClaim={healthClaim}
            onClose={() => setIsDetailsEditModalOpen(false)}
            onSuccess={() => {
              setIsDetailsEditModalOpen(false);
              handleReload();
            }}
          />
        </>
      )}
    </>
  );
};

export { HealthClaimDetails };
